// Screen 3: Paper Detail Page — premium scholarly interface.
// Layout: sidebar | (topbar + 2-col main: hero+tabs+sections | right contextual rail)

import { usePapers } from '../src/lib/use-papers.js';
import { useAuth } from '../src/lib/auth.jsx';
import { useAnalysis, AGENT_LABELS } from '../src/lib/use-analysis.js';

function DetailScreen({ params }) {
  const { t, lang } = useT();
  const nav = React.useContext(NavContext);
  const auth = useAuth();
  const { papers } = usePapers();
  // Look up by URL id first; if absent or unknown, fall back to the first
  // paper so the demo remains navigable for anonymous users.
  const id = params?.id ?? nav?.params?.id ?? null;
  const paper = (id && papers.find((p) => p.id === id))
    ?? papers[0]
    ?? { title: 'no paper', authors: [], summary: '' };

  // Analysis is only enabled for the user's own (D1-backed) papers; the seed
  // demo papers don't have a stable id on the server.
  const canAnalyse = !!paper?._db && !!auth?.user;
  const analysis = useAnalysis(canAnalyse ? paper.id : null);
  const [savedNoteId, setSavedNoteId] = React.useState(null);

  async function saveAnalysisToWiki() {
    if (!auth.user) { auth.signIn(); return; }
    const order = ['synthesis', 'summary', 'critique', 'implementation', 'related'];
    const body = [
      `# 📊 심층 분석 — ${paper.title}`,
      paper.arxiv ? `*arXiv ${paper.arxiv}*` : '',
      ...order.map((id) => {
        const a = analysis.agents[id];
        if (!a?.content) return '';
        const label = AGENT_LABELS[id]?.ko ?? id;
        return `\n## ${label}\n\n${a.content}`;
      }),
    ].filter(Boolean).join('\n');

    try {
      const res = await fetch('/api/notes', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          paper_id: paper.id,
          body,
          anchor: { kind: 'analysis', agents: order },
        }),
      });
      const j = await res.json();
      if (j?.ok) setSavedNoteId(j.data?.id ?? true);
    } catch {}
  }

  // DetailTabs needs to be a forward decl; declared below.
  return (
    <div className="pp pp-screen" data-screen-label="03 Paper detail">
      <Sidebar active="explore"/>

      <div className="pp-main">
        <TopBar
          breadcrumbs={<Breadcrumb items={[t('crumb.explore'), '4DGS', 'GS-DiT']}/>}
          search="Ask about this paper…"
        />

        <div className="pp-content">
          <div className="pp-detail-center scroll">
            {/* Hero */}
            <div className="pp-detail-hero">
              <div className="glow-band" style={{ height: 180, opacity: 0.3, zIndex: 0 }}/>
              <div className="row gap-2" style={{ position: 'relative', flexWrap: 'wrap', zIndex: 1 }}>
                <span className="chip accent">CVPR 2026</span>
                <span className="chip cyan"><I name="check" size={10}/> {t('det.peer')}</span>
                <span className="chip violet"><I name="flame" size={10}/> {t('det.top')}</span>
                <span className="chip ok"><I name="github" size={10}/> {t('det.coderel')}</span>
                <span className="mono" style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--fg-3)' }}>arXiv 2605.04812 · doi 10.1109/CVPR.2026.04812</span>
              </div>

              <h1 className="serif" style={{
                fontSize: 34, lineHeight: 1.1, letterSpacing: '-0.018em',
                margin: '14px 0 14px', color: 'var(--fg-0)', position: 'relative', maxWidth: 820,
                zIndex: 1,
              }}>{paper.title}</h1>

              <div className="row gap-3" style={{ position: 'relative', flexWrap: 'wrap', alignItems: 'center', zIndex: 1 }}>
                <Avatars names={paper.authors} size={22}/>
                <span style={{ fontSize: 13, color: 'var(--fg-1)' }} className="ellipsis">{paper.authors.join(', ')}</span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>Berkeley · Google Research</span>
              </div>

              <div className="row gap-2" style={{ marginTop: 18, position: 'relative', flexWrap: 'wrap', zIndex: 1 }}>
                <button className="btn primary" onClick={() => nav.navigate && nav.navigate('reader', paper?.id ? { id: paper.id } : undefined)}><I name="external" size={13}/> {t('det.openpdf')}</button>
                {canAnalyse && (
                  <button
                    className="btn primary"
                    onClick={() => analysis.run()}
                    disabled={analysis.status === 'running'}
                    title={lang === 'ko' ? '5개 에이전트로 분석' : 'Run 5-agent analysis'}
                  >
                    <I name="sparkles" size={13}/>
                    {analysis.status === 'running'
                      ? (lang === 'ko' ? ' 분석 중…' : ' Analysing…')
                      : (lang === 'ko' ? ' 심층 분석' : ' Deep analysis')}
                  </button>
                )}
                <button className="btn"><I name="bookmark" size={13}/> {t('det.save')}</button>
                <button className="btn"><I name="plus" size={13}/> {t('det.addproj')}</button>
                <button className="btn"><I name="compare" size={13}/> {t('det.compare')}</button>
                <button className="btn ghost"><I name="copy" size={13}/> {t('det.cite')}</button>
                <button className="btn ghost"><I name="github" size={13}/> {t('det.repo')}</button>
                <button className="iconbtn" style={{ marginLeft: 'auto' }}><I name="more" size={14}/></button>
              </div>
            </div>

            {/* Analysis results (5-agent harness) */}
            {(analysis.status === 'running' || analysis.status === 'done' || analysis.status === 'error') && (
              <section className="pp-section">
                <div className="row gap-2" style={{ alignItems: 'baseline', marginBottom: 12 }}>
                  <I name="sparkles" size={14} style={{ color: 'var(--acc-violet)' }}/>
                  <span className="serif" style={{ fontSize: 18 }}>
                    {lang === 'ko' ? '심층 분석' : 'Deep analysis'}
                  </span>
                  <span className="chip" style={{ height: 18, fontSize: 10 }}>
                    {analysis.status === 'running'
                      ? (lang === 'ko' ? '진행 중' : 'running')
                      : analysis.status === 'done'
                        ? (lang === 'ko' ? '완료' : 'done')
                        : (lang === 'ko' ? '오류' : 'error')}
                  </span>
                  {analysis.status === 'done' && (
                    <button
                      className="btn sm primary"
                      style={{ marginLeft: 'auto' }}
                      onClick={saveAnalysisToWiki}
                      disabled={!!savedNoteId}
                    >
                      <I name="book" size={11}/>{' '}
                      {savedNoteId
                        ? (lang === 'ko' ? '위키에 저장됨' : 'Saved to wiki')
                        : (lang === 'ko' ? '위키에 저장' : 'Save to wiki')}
                    </button>
                  )}
                </div>
                {analysis.error && (
                  <div style={{ color: 'var(--warn)', fontSize: 12, marginBottom: 8 }}>
                    {analysis.error.message}
                  </div>
                )}
                <div className="pp-analysis-grid">
                  {['synthesis', 'summary', 'critique', 'implementation', 'related'].map((id) => {
                    const a = analysis.agents[id];
                    const label = lang === 'ko' ? AGENT_LABELS[id].ko : AGENT_LABELS[id].en;
                    return (
                      <div key={id} className={'pp-ai-card' + (id === 'synthesis' ? ' is-synthesis' : '')}>
                        <div className="row gap-2" style={{ alignItems: 'baseline', marginBottom: 8 }}>
                          <I name={id === 'synthesis' ? 'sparkles' : 'figure'} size={12}
                             style={{ color: id === 'synthesis' ? 'var(--acc-violet)' : 'var(--acc-indigo)' }}/>
                          <span style={{ fontSize: 11, color: id === 'synthesis' ? 'var(--acc-violet)' : 'var(--acc-indigo)',
                                          textTransform: 'uppercase', letterSpacing: '0.08em' }} className="mono">
                            {label}
                          </span>
                          <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', marginLeft: 'auto' }}>
                            {a?.done ? (a.error ? 'error' : 'ok') : '…'}
                          </span>
                        </div>
                        {a?.error ? (
                          <div style={{ fontSize: 12, color: 'var(--warn)' }}>{a.error}</div>
                        ) : (
                          <div style={{
                            fontSize: 12.5, color: 'var(--fg-1)', lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                          }}>
                            {a?.content || (
                              <span style={{ color: 'var(--fg-3)' }}>
                                {analysis.status === 'running'
                                  ? (lang === 'ko' ? '대기 중…' : 'pending…')
                                  : '—'}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Tabs */}
            <DetailTabs tabs={[t('det.tab.overview'), t('det.tab.summary'), t('det.tab.why'), t('det.tab.critique'), t('det.tab.novelty'), t('det.tab.qa'), t('det.tab.related'), t('det.tab.notes'), t('det.tab.bench'), t('det.tab.code'), t('det.tab.proj'), t('det.tab.order')]}/>

            {/* Sections */}
            <div className="pp-detail-sections">
              {/* Worth reading? */}
              <section className="pp-section">
                <SectionHead kicker="A" title={t('det.eval.title')} sub={t('det.eval.sub')}/>
                <div className="pp-eval-grid">
                  <EvalCard label={t('det.eval.relevance')} value={0.94} color="var(--acc-indigo)" note={t('det.eval.note.rel')}/>
                  <EvalCard label={t('det.eval.novelty')}   value={0.81} color="var(--acc-violet)" note={t('det.eval.note.nov')}/>
                  <EvalCard label={t('det.eval.impl')}      value={0.74} color="var(--acc-cyan)"   note={t('det.eval.note.imp')}/>
                  <EvalCard label={t('det.eval.signif')}    value={0.86} color="var(--ok)"          note={t('det.eval.note.sig')}/>
                  <EvalCard label={t('det.eval.impact')}    value={0.79} color="#E8B86A"            note={t('det.eval.note.impact')}/>
                  <EvalCard label={t('det.eval.diff')}      value={0.62} color="var(--warn)"      note={t('det.eval.note.dif')}/>
                </div>
                <div className="pp-recommend-bar">
                  <I name="sparkles" size={14} style={{ color: 'var(--acc-violet)' }}/>
                  <div className="col grow gap-1">
                    <div style={{ fontSize: 12.5, color: 'var(--fg-0)' }}>{t('det.rec.headline')}</div>
                    <div style={{ fontSize: 11, color: 'var(--fg-2)' }}>{t('det.rec.sub')}</div>
                  </div>
                  <button className="btn sm primary"><I name="bookmark" size={12}/> {t('det.rec.add')}</button>
                </div>
              </section>

              {/* Why recommended */}
              <section className="pp-section">
                <SectionHead kicker="B" title={t('det.why.title')}/>
                <div className="pp-why-list">
                  <WhyItem icon="kanban" tone="indigo"
                    title={t('det.why1.title')}
                    body={t('det.why1.body')}/>
                  <WhyItem icon="book" tone="violet"
                    title={t('det.why2.title')}
                    body={t('det.why2.body')}/>
                  <WhyItem icon="graph" tone="cyan"
                    title={t('det.why3.title')}
                    body={t('det.why3.body')}/>
                  <WhyItem icon="user" tone="warn"
                    title={t('det.why4.title')}
                    body={t('det.why4.body')}/>
                </div>
              </section>

              {/* Reading order */}
              <section className="pp-section">
                <SectionHead kicker="C" title={t('det.order.title')} sub={t('det.order.sub')}/>
                <div className="pp-reading-order">
                  <div className="pp-reading-col">
                    <div className="pp-reading-head">
                      <I name="chevronLeft" size={12}/>
                      <span style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-3)' }}>{t('det.order.before')}</span>
                    </div>
                    <ReadOrderRow title="3D Gaussian Splatting for Real-Time Radiance Field Rendering" venue="SIGGRAPH 23" tag={t('det.order.tag.prereq')} you={t('det.order.read')}/>
                    <ReadOrderRow title="Deformable 3D Gaussians for High-Fidelity Monocular Dynamic Scenes" venue="CVPR 24" tag={t('det.order.tag.prereq')} you={t('det.order.read')}/>
                    <ReadOrderRow title="DiT: Scalable Diffusion Models with Transformers" venue="ICCV 23" tag={t('det.order.tag.prior')} you={t('det.order.unread')}/>
                  </div>
                  <div className="pp-reading-col" style={{ borderLeft: '1px solid var(--border-1)' }}>
                    <div className="pp-reading-head">
                      <span style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-3)' }}>{t('det.order.after')}</span>
                      <I name="chevronRight" size={12}/>
                    </div>
                    <ReadOrderRow title="4DGS-Cache: Deformation-Field Caching at 120 Hz" venue="SIGGRAPH 26" tag={t('det.order.tag.follow')} you={t('det.order.queue')}/>
                    <ReadOrderRow title="Stop Citing PSNR: A Perceptual Benchmark for 4D" venue="arXiv 26" tag={t('det.order.tag.crit')} you={t('det.order.unread')}/>
                    <ReadOrderRow title="GS-Edit: Differentiable Editing of Gaussian Splat Fields" venue="ECCV 26" tag={t('det.order.tag.app')} you={t('det.order.unread')}/>
                  </div>
                </div>
              </section>

              {/* Related benchmark */}
              <section className="pp-section">
                <SectionHead kicker="D" title={t('det.bench.title')}
                  action={<button className="btn sm ghost" onClick={() => nav.navigate && nav.navigate('bench')}><I name="external" size={12}/> {t('det.bench.open')}</button>}/>
                <div className="pp-bench-mini">
                  <div className="pp-bench-mini-head row gap-3" style={{ padding: '8px 12px', background: 'var(--bg-3)', fontSize: 10.5, color: 'var(--fg-3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    <div style={{ flex: 2 }}>Method</div>
                    <div style={{ flex: 1 }}>Dataset</div>
                    <div style={{ flex: 1 }}>Condition</div>
                    <div style={{ flex: 1 }}>PSNR ↑</div>
                    <div style={{ flex: 1 }}>FPS ↑</div>
                    <div style={{ flex: 1 }}>Code</div>
                  </div>
                  {[
                    { m: 'GS-DiT (this)', d: 'D-NeRF', c: '8 views', psnr: '31.74', fps: '84',  code: true,  hl: true },
                    { m: 'Def-3DGS',     d: 'D-NeRF', c: '8 views', psnr: '30.42', fps: '27',  code: true },
                    { m: 'K-Planes',     d: 'D-NeRF', c: '8 views', psnr: '29.61', fps: '0.3', code: true },
                    { m: '4DGS-Cache',   d: 'D-NeRF', c: '8 views', psnr: '31.21', fps: '120', code: false },
                  ].map((r, i) => (
                    <div key={i} className="row gap-3" style={{ padding: '8px 12px', borderTop: '1px solid var(--border-1)', background: r.hl ? 'rgba(136,150,255,0.06)' : 'transparent' }}>
                      <div style={{ flex: 2, fontSize: 12, color: r.hl ? 'var(--acc-indigo)' : 'var(--fg-0)', fontWeight: r.hl ? 500 : 400 }}>{r.m}</div>
                      <div style={{ flex: 1, fontSize: 11.5, color: 'var(--fg-2)' }} className="mono">{r.d}</div>
                      <div style={{ flex: 1, fontSize: 11.5, color: 'var(--fg-2)' }} className="mono">{r.c}</div>
                      <div style={{ flex: 1, fontSize: 12, color: 'var(--fg-0)' }} className="mono">{r.psnr}</div>
                      <div style={{ flex: 1, fontSize: 12, color: 'var(--fg-0)' }} className="mono">{r.fps}</div>
                      <div style={{ flex: 1, fontSize: 11 }}>{r.code ? <span className="chip ok" style={{ height: 18 }}><I name="check" size={10}/>yes</span> : <span className="chip ghost" style={{ height: 18 }}>no</span>}</div>
                    </div>
                  ))}
                </div>
              </section>

              {/* E · Reviewer-style critique */}
              <CritiqueSection/>

              {/* F · Novelty decomposition */}
              <NoveltySection/>

              {/* G · Q&A history for this paper */}
              <PaperQASection paperId={paper.id}/>
            </div>
          </div>

          {/* Right contextual rail */}
          <RailRight id="detail-rail" defaultWidth={360} min={300} max={500} className="pp-detail-rail scroll">
            <div className="card" style={{ padding: 14 }}>
              <div className="section-title" style={{ marginBottom: 8 }}>{t('det.rail.sum')}</div>
              <div style={{ fontSize: 12.5, color: 'var(--fg-1)', lineHeight: 1.6 }}>
                Couples deformable 3D Gaussian splatting with a small diffusion-transformer prior over per-Gaussian motion. The prior is distilled once at training; at inference it disappears, so rendering stays a single forward splat. Key trick is a <span style={{ color: 'var(--fg-0)' }}>quantized canonical-id cache</span> that lets the system skip per-frame MLP evaluation and reach 84 FPS on a single 4090.
              </div>
              <div className="col gap-2" style={{ marginTop: 10 }}>
                <SummaryBullet text="New SoTA on D-NeRF (31.74 PSNR, +1.32)"/>
                <SummaryBullet text="3.1× faster than Deformable-3DGS"/>
                <SummaryBullet text="Robust to thin-geometry flicker"/>
                <SummaryBullet text="Single-GPU training in 11 hours"/>
              </div>
            </div>

            <div className="card" style={{ padding: 14 }}>
              <div className="section-title" style={{ marginBottom: 8 }}>{t('det.rail.conn')}</div>
              <ConnectionCard project="Server-rack digital twin" color="#8896FF" score={0.94}
                bullets={[
                  'Thin metallic geometry handling (your top failure mode)',
                  'FPS budget compatible with 30 Hz interactive twin',
                  'Sparse-view stable — fits your 4-cam rig',
                ]}/>
              <ConnectionCard project="4D reconstruction (wiki)" color="#B49BFF" score={0.78}
                bullets={[
                  'Adds deformation-field family entry',
                  'Reuses canonical-frame formulation from Park 24',
                ]}/>
            </div>

            <div className="card" style={{ padding: 14 }}>
              <div className="section-title" style={{ marginBottom: 8 }}>{t('det.rail.meth')}</div>
              <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                <span className="tag indigo">deformation field</span>
                <span className="tag indigo">diffusion prior</span>
                <span className="tag indigo">gaussian splat</span>
                <span className="tag">canonical frame</span>
                <span className="tag">vq-cache</span>
                <span className="tag">monocular video</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 10 }} className="mono">
                Trains on 28k clips · MVS preprocessing · L1 + LPIPS + flow consistency
              </div>
            </div>

            <div className="card" style={{ padding: 14 }}>
              <div className="section-title" style={{ marginBottom: 8 }}>{t('det.rail.res')}</div>
              <ResLink icon="github" label="GitHub · tancik/gs-dit"   meta="412 ★ · MIT"/>
              <ResLink icon="external" label="Project page"             meta="gs-dit.berkeley.edu"/>
              <ResLink icon="figure"  label="Pretrained checkpoint"     meta="2.1 GB · D-NeRF"/>
              <ResLink icon="data"    label="Eval dataset (D-NeRF)"     meta="reformatted, 4.4 GB"/>
            </div>
          </RailRight>
        </div>
      </div>
    </div>
  );
}

function DetailTabs({ tabs }) {
  const [active, setActive] = React.useState(0);
  return (
    <div className="pp-detail-tabs">
      {tabs.map((tx, i) => (
        <button key={i} className={'pp-detail-tab ' + (i === active ? 'is-active' : '')} onClick={() => setActive(i)}>{tx}</button>
      ))}
    </div>
  );
}

function EvalCard({ label, value, color, note }) {
  const v = Math.round(value * 100);
  return (
    <div className="pp-eval-card">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-3)' }}>{label}</span>
        <span className="serif" style={{ fontSize: 22, color }}>{v}</span>
      </div>
      <ScoreBar value={value} color={color}/>
      <div style={{ fontSize: 11, color: 'var(--fg-2)', marginTop: 8, lineHeight: 1.45 }}>{note}</div>
    </div>
  );
}

function WhyItem({ icon, tone, title, body }) {
  const map = { indigo: 'var(--acc-indigo)', violet: 'var(--acc-violet)', cyan: 'var(--acc-cyan)', warn: 'var(--warn)' };
  const c = map[tone] || map.indigo;
  return (
    <div className="pp-why-item">
      <div className="pp-why-icon" style={{ background: `rgba(255,255,255,0.02)`, borderColor: `${c}55`, color: c }}><I name={icon} size={14}/></div>
      <div className="col grow gap-1" style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--fg-0)', fontWeight: 500 }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--fg-1)', lineHeight: 1.5 }}>{body}</div>
      </div>
    </div>
  );
}

function ReadOrderRow({ title, venue, tag, you }) {
  const { t } = useT();
  const youColor = you === t('det.order.read') ? 'var(--ok)' : you === t('det.order.queue') ? 'var(--acc-cyan)' : 'var(--fg-3)';
  return (
    <div className="pp-readorder-row">
      <div className="col grow gap-1" style={{ minWidth: 0 }}>
        <div className="ellipsis serif" style={{ fontSize: 13.5, color: 'var(--fg-0)' }}>{title}</div>
        <div className="row gap-2" style={{ fontSize: 10.5 }}>
          <span className="mono" style={{ color: 'var(--fg-3)' }}>{venue}</span>
          <span style={{ color: 'var(--acc-indigo)' }}>{tag}</span>
          <span style={{ color: youColor }}>· {you}</span>
        </div>
      </div>
      <button className="iconbtn"><I name="chevronRight" size={12}/></button>
    </div>
  );
}

function SummaryBullet({ text }) {
  return (
    <div className="row gap-2" style={{ alignItems: 'flex-start' }}>
      <div style={{ width: 4, height: 4, borderRadius: 999, marginTop: 7, background: 'var(--acc-indigo)', boxShadow: '0 0 4px var(--acc-indigo)' }}/>
      <span style={{ fontSize: 12, color: 'var(--fg-1)' }}>{text}</span>
    </div>
  );
}

function ConnectionCard({ project, color, score, bullets }) {
  return (
    <div className="pp-conn-card">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div className="row gap-2">
          <div style={{ width: 6, height: 6, borderRadius: 2, background: color, boxShadow: `0 0 6px ${color}` }}/>
          <span style={{ fontSize: 12, color: 'var(--fg-0)' }}>{project}</span>
        </div>
        <span className="mono" style={{ fontSize: 11, color }}>{Math.round(score * 100)}</span>
      </div>
      <ScoreBar value={score} color={color}/>
      <div className="col gap-1" style={{ marginTop: 8 }}>
        {bullets.map((b, i) => (
          <div key={i} style={{ fontSize: 11, color: 'var(--fg-2)' }}>· {b}</div>
        ))}
      </div>
    </div>
  );
}

function ResLink({ icon, label, meta }) {
  return (
    <div className="pp-reslink">
      <div className="pp-reslink-icon"><I name={icon} size={13}/></div>
      <div className="col grow" style={{ minWidth: 0 }}>
        <div className="ellipsis" style={{ fontSize: 12, color: 'var(--fg-0)' }}>{label}</div>
        <div className="ellipsis mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{meta}</div>
      </div>
      <I name="external" size={12} style={{ color: 'var(--fg-3)' }}/>
    </div>
  );
}

if (!document.getElementById('pp-detail-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-detail-styles';
  s.textContent = `
  .pp-detail-center { flex: 1; min-width: 0; padding: 24px 32px 40px; overflow: auto; display: flex; flex-direction: column; gap: 28px; }

  /* Five-agent analysis grid: synthesis card spans full width on top. */
  .pp-analysis-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  .pp-analysis-grid .pp-ai-card.is-synthesis {
    grid-column: 1 / -1;
    border-color: rgba(180,155,255,0.45);
    box-shadow: 0 0 0 1px rgba(180,155,255,0.18);
  }
  @media (max-width: 1280px) { .pp-analysis-grid { grid-template-columns: 1fr; } }
  .pp-detail-rail { width: 360px; flex-shrink: 0; padding: 24px 18px 28px; border-left: 1px solid var(--border-1); background: var(--bg-1); overflow: auto; display: flex; flex-direction: column; gap: 14px; }
  .pp-detail-hero { position: relative; padding-bottom: 16px; }
  .pp-detail-tabs {
    display: flex; gap: 4px;
    border-bottom: 1px solid var(--border-1);
    overflow-x: auto;
  }
  .pp-detail-tab {
    padding: 9px 14px;
    font-size: 12.5px; color: var(--fg-2);
    border-bottom: 2px solid transparent;
    cursor: pointer; white-space: nowrap;
    margin-bottom: -1px;
  }
  .pp-detail-tab:hover { color: var(--fg-0); }
  .pp-detail-tab.is-active { color: var(--fg-0); border-bottom-color: var(--acc-indigo); box-shadow: 0 4px 16px -8px var(--acc-indigo); }
  .pp-detail-sections { display: flex; flex-direction: column; gap: 32px; }
  .pp-section { display: flex; flex-direction: column; }
  .pp-eval-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .pp-eval-card {
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 10px;
    padding: 12px;
  }
  .pp-recommend-bar {
    margin-top: 14px;
    padding: 12px 14px;
    display: flex; align-items: center; gap: 12px;
    border: 1px solid rgba(180,155,255,0.30);
    background: linear-gradient(90deg, rgba(180,155,255,0.08), rgba(136,150,255,0.04));
    border-radius: 10px;
  }
  .pp-why-list { display: flex; flex-direction: column; gap: 12px; }
  .pp-why-item {
    display: flex; gap: 12px;
    padding: 12px 14px;
    border: 1px solid var(--border-1);
    background: var(--bg-2);
    border-radius: 10px;
  }
  .pp-why-icon {
    width: 30px; height: 30px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    border: 1px solid;
    flex-shrink: 0;
  }
  .pp-reading-order {
    display: grid; grid-template-columns: 1fr 1fr;
    border: 1px solid var(--border-1);
    border-radius: 10px;
    background: var(--bg-2);
    overflow: hidden;
  }
  .pp-reading-col { padding: 12px 14px; display: flex; flex-direction: column; gap: 4px; }
  .pp-reading-head { display: flex; align-items: center; gap: 6px; padding-bottom: 8px; margin-bottom: 4px; color: var(--fg-3); }
  .pp-readorder-row {
    display: flex; align-items: center; gap: 8px;
    padding: 9px 8px; border-radius: 6px;
    border-top: 1px solid var(--border-0);
    cursor: pointer;
  }
  .pp-readorder-row:hover { background: rgba(255,255,255,0.025); }
  .pp-bench-mini {
    border: 1px solid var(--border-1); border-radius: 10px; overflow: hidden;
  }
  .pp-conn-card {
    padding: 10px 12px; margin-top: 10px;
    background: var(--bg-3);
    border: 1px solid var(--border-1);
    border-radius: 8px;
  }
  .pp-conn-card:first-child { margin-top: 0; }
  .pp-reslink {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 6px;
    border-bottom: 1px solid var(--border-0);
    cursor: pointer;
  }
  .pp-reslink:last-child { border-bottom: 0; }
  .pp-reslink:hover { background: rgba(255,255,255,0.02); }
  .pp-reslink-icon {
    width: 26px; height: 26px; border-radius: 6px;
    background: var(--bg-3); border: 1px solid var(--border-1);
    color: var(--fg-1);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  `;
  document.head.appendChild(s);
}

export { DetailScreen };
